/**
 * Block-crack overlay (vanilla "crumbling" render type).
 *
 *   const crack = new BreakOverlay();
 *   scene.add(crack.object3d);
 *   crack.set(x, y, z, progress01, boxes?)   // boxes: optional flat 1/16-unit boxes (x0,y0,z0,x1,y1,z1 ...), default whole cell
 *   crack.clear();
 *
 * destroy_stage_0..9 is chosen from progress. Vanilla blends (DST_COLOR, SRC_COLOR) in gamma space,
 * i.e. result = 2 * tex * dst; the scene here is linear HDR, so the equivalent per-pixel multiplier
 * is (2 * tex_srgb)^2.2 with a plain dst*src blend.
 */

import * as THREE from 'three';

const VERT = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;
const FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uMap;
in vec2 vUv;
out vec4 fragColor;
void main() {
  vec4 t = texture(uMap, vUv);
  if (t.a < 0.1) discard;
  fragColor = vec4(pow(2.0 * t.rgb, vec3(2.2)), 1.0);
}
`;

const FULL = new Float32Array([0, 0, 0, 16, 16, 16]);
const GROW = 0.002;

export class BreakOverlay {
  readonly object3d = new THREE.Group();
  private readonly mesh: THREE.Mesh;
  private readonly mat: THREE.ShaderMaterial;
  private readonly textures: (THREE.Texture | null)[] = new Array(10).fill(null);
  private geoKey = '';
  private stage = -1;

  constructor() {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      glslVersion: THREE.GLSL3,
      uniforms: { uMap: { value: null } },
      transparent: true,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.DstColorFactor,
      blendDst: THREE.ZeroFactor,
      blendSrcAlpha: THREE.ZeroFactor,
      blendDstAlpha: THREE.OneFactor,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5;
    this.mesh.visible = false;
    this.object3d.add(this.mesh);
    const loader = new THREE.TextureLoader();
    for (let i = 0; i < 10; i++) {
      loader.load(`mc/destroy/destroy_stage_${i}.png`, (t) => {
        t.colorSpace = THREE.NoColorSpace; // raw gamma values: the shader linearises the multiplier itself
        t.magFilter = t.minFilter = THREE.NearestFilter;
        t.generateMipmaps = false;
        this.textures[i] = t;
        if (i === this.stage) this.mat.uniforms.uMap.value = t;
      }, undefined, () => { /* missing: overlay stays invisible */ });
    }
  }

  get active(): boolean { return this.mesh.visible; }

  set(x: number, y: number, z: number, progress: number, boxes: Float32Array = FULL): void {
    const stage = Math.max(0, Math.min(9, Math.floor(progress * 10)));
    if (stage !== this.stage) {
      this.stage = stage;
      this.mat.uniforms.uMap.value = this.textures[stage];
    }
    let key = '';
    for (let i = 0; i < boxes.length; i++) key += boxes[i] + ',';
    if (key !== this.geoKey) {
      this.geoKey = key;
      this.mesh.geometry.dispose();
      this.mesh.geometry = build(boxes);
    }
    this.mesh.position.set(x, y, z);
    this.mesh.visible = this.textures[stage] !== null;
  }

  clear(): void { this.mesh.visible = false; this.stage = -1; }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mat.dispose();
    for (const t of this.textures) t?.dispose();
  }
}

/** Slightly grown boxes, each face sampling the whole texture projected along its axis in block space. */
function build(boxes: Float32Array): THREE.BufferGeometry {
  const pos: number[] = [], uv: number[] = [], idx: number[] = [];
  for (let b = 0; b + 5 < boxes.length; b += 6) {
    const lo = [boxes[b] / 16 - GROW, boxes[b + 1] / 16 - GROW, boxes[b + 2] / 16 - GROW];
    const hi = [boxes[b + 3] / 16 + GROW, boxes[b + 4] / 16 + GROW, boxes[b + 5] / 16 + GROW];
    for (let a = 0; a < 3; a++) {
      const u = (a + 1) % 3, w = (a + 2) % 3;
      for (const side of [0, 1]) {
        const base = pos.length / 3;
        const corners = [[0, 0], [1, 0], [1, 1], [0, 1]];
        for (const [i, j] of corners) {
          const p = [0, 0, 0];
          p[a] = side ? hi[a] : lo[a];
          p[u] = i ? hi[u] : lo[u];
          p[w] = j ? hi[w] : lo[w];
          pos.push(p[0], p[1], p[2]);
          uv.push(p[u] - Math.floor(lo[u] + GROW), 1 - (p[w] - Math.floor(lo[w] + GROW)));
        }
        // outward-facing winding: (u x w) points +a for a = 0..2 cyclic, flip for the low side
        if (side) idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
        else idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}
